import {
  UnblockButton,
  InputStepHoverCard,
  InputStepUser,
} from "~/components/Playground/Node/InputStepNode";
import {
  BlockStep,
  InputStep,
  Outcome,
  State,
  hasReceivedInput,
  isCanceled,
  isInputDisabled,
  isIgnored,
} from "~/lib/pipeline";
import { StateIcon } from "~/components/Playground/Node/BaseNode/StateIcon";
import { textColorForStep } from "~/components/Playground/Node/colors";
import { twMerge } from "tailwind-merge";
import Emojify from "~/components/shared/Emojify";
import classNames from "classnames";
import { resolveStepUrl } from "~/components/build/Show/lib/urlForView";
import useCurrentJobOrStep from "~/components/build/Show/lib/useCurrentJobOrStep";
import { StepLink } from "~/components/build/Show/components/Sidebar/TreeNode/StepLink";
import * as HoverCard from "~/components/Playground/Node/StepHoverCard";
import { TreeNode, TreeNodeType } from "~/components/build/Show/components/Sidebar/TreeNode";
import { useStepDependencies } from "~/components/build/Show/lib/useStepDependencies";

interface Props {
  step: InputStep | BlockStep;
  depth?: number;
}

/** @deprecated Use `~/components/build/Show/pages/steps/list/components/TreeNode/InputStepItem` instead. */
export function InputStepItem({ step, depth = 0 }: Props) {
  const { currentStep } = useCurrentJobOrStep();
  const { missingDependencies } = useStepDependencies(step.uuid);

  const isStepIgnored = isIgnored(step);
  const url = resolveStepUrl(step.uuid, { open: false });
  const label = step.label || "Continue";

  if (isStepIgnored) {
    return (
      <StepLink
        state={step.state}
        to={url}
        active={step.uuid === currentStep?.uuid}
        depth={depth}
        className={isStepIgnored ? "opacity-60" : ""}
        disabled={true}
        title="Skipped steps cannot be viewed"
      >
        <div className="flex min-w-0 flex-auto py-1">
          <div className="w-full max-w-full truncate font-medium">
            <Emojify text={label} />
          </div>
        </div>

        <div className="flex shrink-0 items-center justify-center self-stretch">
          <StateIcon
            state={step.state}
            outcome={step.outcome}
            missingDependencies={missingDependencies}
            className={twMerge("-mr-0.5", textColorForStep({ ...step, missingDependencies }))}
          />
        </div>
      </StepLink>
    );
  }

  if (hasReceivedInput(step)) {
    return (
      <HoverCard.Root>
        <HoverCard.Portal>
          <InputStepHoverCard step={step} className="w-fit" collisionPadding={5} side="right" />
        </HoverCard.Portal>

        <HoverCard.Trigger asChild={true}>
          <div className="w-full">
            <StepLink
              state={step.state}
              to={url}
              active={step.uuid === currentStep?.uuid}
              depth={depth}
            >
              <div className="flex min-w-0 flex-auto py-1">
                <div className="w-full max-w-full truncate font-medium">
                  <Emojify text={label} />
                </div>
              </div>

              <span className="flex w-5 shrink-0 items-center justify-center self-stretch">
                <InputStepUser step={step} />
              </span>

              <div className="flex shrink-0 items-center justify-center self-stretch">
                <StateIcon
                  state={State.Finished}
                  outcome={Outcome.Passed}
                  missingDependencies={missingDependencies}
                  className={twMerge(
                    "-mr-0.5",
                    textColorForStep({
                      state: State.Finished,
                      outcome: Outcome.Passed,
                      missingDependencies,
                    }),
                  )}
                />
              </div>
            </StepLink>
          </div>
        </HoverCard.Trigger>
      </HoverCard.Root>
    );
  }

  const inputRequiredBadge =
    isCanceled(step) || isInputDisabled(step) ? (
      <span className="rounded-sm bg-gray-100 px-1.5 py-0.5 text-xs font-medium opacity-50">
        Input required
      </span>
    ) : (
      <span className="rounded-sm bg-purple-100 px-1.5 py-0.5 text-xs font-medium text-purple-600">
        Input required
      </span>
    );

  const content = (
    <>
      <div className="flex min-w-0 flex-auto py-1">
        <div className="w-full max-w-full truncate font-medium">
          <Emojify text={label} />
        </div>
      </div>

      <span className="flex shrink-0 items-center justify-center gap-1 self-stretch">
        {inputRequiredBadge}
        <StateIcon
          state={step.state}
          outcome={step.outcome}
          missingDependencies={missingDependencies}
          className={twMerge("-mr-0.5", textColorForStep({ ...step, missingDependencies }))}
        />
      </span>
    </>
  );

  if (isCanceled(step) || isInputDisabled(step)) {
    return (
      <StepLink
        state={step.state}
        to={url}
        active={step.uuid === currentStep?.uuid}
        depth={depth}
        disabled={true}
        className={isInputDisabled(step) ? "cursor-not-allowed" : undefined}
      >
        {content}
      </StepLink>
    );
  }

  return (
    <UnblockButton
      step={step}
      className={classNames("unstyled-button hover:bg-purple-100-50-white w-full rounded-md pr-2", {
        "pl-2": depth === 0,
        "pl-[calc(1.5rem_+_2px)]": depth === 1,
      })}
    >
      <div className="flex min-w-0 flex-auto items-baseline gap-1">{content}</div>
    </UnblockButton>
  );
}

/** @deprecated Use `~/components/build/Show/pages/steps/list/components/TreeNode/InputStepItem` instead. */
export const createInputStepNode = (step: InputStep | BlockStep): TreeNode => ({
  id: step.uuid,
  type: TreeNodeType.StepInput,
  render: (props) => <InputStepItem step={step} {...props} />,
});
